import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@material-ui/lab/Autocomplete';
import moment from 'moment-timezone';
import React from 'react';
import {
   AccountStore,
   Component,
   IssueActions,
   IssueStore,
   LocationStore,
   SettingsStore,
   StaffMemberStore,
   TeamStore
} from '../../../client';
import { constants, lodash as _, newGuid } from '../../../common';
import FlagSelector from '../../../components/issues/FlagSelector';
import IssueAssignmentDialog, { buildAssignmentWho } from '../IssuePage/IssueAssignmentDialog';
import IssueTagsDialog from '../IssuePage/IssueTagsDialog';
import { normalizeTagName } from '../IssuePage/issueTags';
import { buildLocalIssueTag, buildRaiseIssuePayload, getIssueCreateErrors, hasIssueCreateErrors } from './issueCreate';
import './style.scss';

const { tagTypes } = constants;

const NumberButton = ({ number }) => <Avatar className='number size-30 bg-secondary'>{number}</Avatar>;

const assignmentCaption = (selection, staff, teams) => {
   if (!selection) return 'Unassigned';
   if (selection.type === 'team') {
      const team = _.find(teams || [], (item) => String(item.no) === String(selection.no));
      return (team && team.name) || `Team #${selection.no}`;
   }
   const member = _.find(staff || [], (item) => String(item.noUser == null ? item.no : item.noUser) === String(selection.no));
   return (member && member.user && member.user.name) || (member && member.name) || `Staff #${selection.no}`;
};

class IssueNew extends Component {
   constructor(props) {
      super(props);
      this.state = {
         ...this.state,
         title: '',
         description: '',
         location: null,
         noPriority: null,
         assignment: null,
         selectedTags: [],
         localTags: [],
         submitted: false,
         assignmentDialogOpen: false,
         tagsDialogOpen: false
      };
      this.stores = [IssueStore, LocationStore, SettingsStore, AccountStore, StaffMemberStore, TeamStore];
      this.storeKeys = [
         'raiseIssueSaving',
         'raiseIssueError',
         'raiseIssueCompletedAt',
         'raisedIssue',
         'locations',
         'priorities',
         'allTags',
         'authUser',
         'staff',
         'teams'
      ];
   }

   componentDidMount() {
      if (super.componentDidMount) super.componentDidMount();
      IssueActions.resetRaiseIssueSync();
   }

   componentDidUpdate(prevProps, prevState) {
      if (super.componentDidUpdate) super.componentDidUpdate(prevProps, prevState);
      if (this.state.raiseIssueCompletedAt && this.state.raiseIssueCompletedAt !== prevState.raiseIssueCompletedAt) {
         const issue = this.state.raisedIssue || {};
         if (issue.noIssue != null) this.props.history.replace(`/app/observe/issues/${issue.noIssue}`);
      }
   }

   _close = () => {
      if (!this.state.raiseIssueSaving) this.props.history.push('/app/observe/issues');
   };

   _issueTags = () => {
      const existingTags = _.filter(
         this.state.allTags || [],
         (tag) => !tag.deleted && (tag.noTagType == null || tag.noTagType === tagTypes.ISSUE.id)
      );
      return _.uniqBy([...existingTags, ...(this.state.localTags || [])], (tag) => normalizeTagName(tag.name));
   };

   _createLocalTag = (tagName) => {
      const name = String(tagName || '').trim();
      const normalized = normalizeTagName(name);
      const existing = _.find(this._issueTags(), (tag) => normalizeTagName(tag.name) === normalized);
      if (existing) return existing;

      const tag = buildLocalIssueTag(name, tagTypes.ISSUE.id);
      this.setState({ localTags: [...(this.state.localTags || []), tag] });
      return tag;
   };

   _fakeIssue = () => ({
      who: buildAssignmentWho(this.state.assignment),
      tags: this.state.selectedTags
   });

   _submit = () => {
      if (this.state.raiseIssueSaving) return;
      this.setState({ submitted: true });
      const errors = getIssueCreateErrors(this.state);
      if (hasIssueCreateErrors(errors)) return;

      const authUser = this.state.authUser || {};
      const zonedFor = authUser.timezoneCode || authUser.timezone || moment.tz.guess();
      IssueActions.beginRaiseIssueSync();
      IssueActions.raiseIssue(
         buildRaiseIssuePayload({
            idIssue: newGuid(),
            title: this.state.title,
            description: this.state.description,
            location: this.state.location,
            zonedFor,
            selectedTags: this.state.selectedTags,
            noPriority: this.state.noPriority,
            who: buildAssignmentWho(this.state.assignment),
            utcNow: moment.utc().toDate()
         })
      );
   };

   render() {
      const busy = Boolean(this.state.raiseIssueSaving);
      const submitted = this.state.submitted;
      const locations = this.state.locations || [];
      const priorities = _.filter(this.state.priorities || [], (flag) => !flag.deleted);
      const tags = this._issueTags();
      const currentAssignmentCaption = assignmentCaption(this.state.assignment, this.state.staff, this.state.teams);

      return (
         <Dialog fullScreen open classes={{ paper: 'dialog-paper' }} onClose={busy ? undefined : this._close}>
            <div className='IssueNew animated slideInUpTiny animation-duration-6'>
               <form
                  onSubmit={(event) => {
                     event.preventDefault();
                     this._submit();
                  }}>
                  <div className='main-title'>
                     <h1>Raise an Issue</h1>
                  </div>

                  <Card raised className='issue-new-card shadow border-0'>
                     <CardContent className='issue-new-card-body'>
                        {this.state.raiseIssueError && (
                           <div className='issue-new-error' role='alert'>
                              {this.state.raiseIssueError}
                           </div>
                        )}

                        <div className='issue-new-section'>
                           <div className='number-wrapper'>
                              <NumberButton number={1} />
                           </div>
                           <div className='section-contents'>
                              <Typography className='section-title'>What's the problem?</Typography>
                              <div className='section-fields'>
                                 <TextField
                                    required
                                    autoFocus
                                    label='Title'
                                    placeholder='e.g. Grinder calibration needed'
                                    variant='outlined'
                                    InputLabelProps={{ shrink: true }}
                                    value={this.state.title}
                                    disabled={busy}
                                    error={submitted && !this.state.title.trim()}
                                    helperText={submitted && !this.state.title.trim() ? 'Title is required.' : ''}
                                    onChange={(event) => this.setState({ title: event.target.value })}
                                    inputRef={(node) => {
                                       this.titleInput = node;
                                    }}
                                 />
                                 <TextField
                                    label='Additional info'
                                    placeholder='e.g. Hopper 1 dosing inconsistent'
                                    variant='outlined'
                                    InputLabelProps={{ shrink: true }}
                                    multiline
                                    minRows={3}
                                    value={this.state.description}
                                    disabled={busy}
                                    onChange={(event) => this.setState({ description: event.target.value })}
                                 />
                              </div>
                           </div>
                        </div>

                        <div className='issue-new-section'>
                           <div className='number-wrapper'>
                              <NumberButton number={2} />
                           </div>
                           <div className='section-contents'>
                              <Typography className='section-title'>Where did this happen?</Typography>
                              <div className='section-fields'>
                                 <Autocomplete
                                    options={locations}
                                    value={this.state.location}
                                    getOptionLabel={(option) => option.fullName || option.name || `Location #${option.no}`}
                                    getOptionSelected={(option, value) => String(option.no) === String(value.no)}
                                    onChange={(event, location) => this.setState({ location })}
                                    disabled={busy}
                                    renderInput={(params) => (
                                       <TextField
                                          {...params}
                                          required
                                          label='Location'
                                          variant='outlined'
                                          error={submitted && !this.state.location}
                                          helperText={submitted && !this.state.location ? 'Location is required.' : ''}
                                       />
                                    )}
                                 />
                                 <TextField
                                    className='lookup-field'
                                    label='Tags'
                                    placeholder='e.g. Grinder'
                                    helperText='Optional labels for the source, equipment, or area'
                                    variant='outlined'
                                    value={
                                       this.state.selectedTags.length ? _.map(this.state.selectedTags, (tag) => tag.name).join(', ') : ''
                                    }
                                    disabled={busy}
                                    onClick={() => this.setState({ tagsDialogOpen: true })}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ readOnly: true }}
                                 />
                              </div>
                           </div>
                        </div>

                        <div className='issue-new-section'>
                           <div className='number-wrapper'>
                              <NumberButton number={3} />
                           </div>
                           <div className='section-contents'>
                              <Typography className='section-title'>How urgent is it?</Typography>
                              <div className='section-fields'>
                                 <FlagSelector
                                    priorities={priorities}
                                    noPriority={this.state.noPriority}
                                    label='Priority'
                                    required
                                    disabled={busy}
                                    helperText={submitted && this.state.noPriority == null ? 'Priority is required.' : ''}
                                    error={submitted && this.state.noPriority == null}
                                    onChange={(noPriority) => this.setState({ noPriority })}
                                 />
                              </div>
                           </div>
                        </div>

                        <div className='issue-new-section'>
                           <div className='number-wrapper'>
                              <NumberButton number={4} />
                           </div>
                           <div className='section-contents'>
                              <Typography className='section-title'>Who owns this?</Typography>
                              <div className='section-fields'>
                                 <TextField
                                    className='lookup-field'
                                    label='Assignment'
                                    variant='outlined'
                                    value={currentAssignmentCaption}
                                    disabled={busy}
                                    onClick={() => this.setState({ assignmentDialogOpen: true })}
                                    InputProps={{ readOnly: true }}
                                 />
                              </div>
                           </div>
                        </div>
                     </CardContent>
                  </Card>

                  <div className='buttons'>
                     <Button
                        type='submit'
                        color='primary'
                        variant='contained'
                        size='large'
                        disabled={busy}
                        className='btn btn-sm bg-primary text-white transform-none btn-raise'>
                        {busy ? 'Raising issue…' : 'Raise issue'}
                     </Button>
                     <span className='or-btn'>or</span>
                     <Button disabled={busy} onClick={this._close} className='btn btn-sm' color='primary'>
                        Cancel
                     </Button>
                  </div>
               </form>
            </div>
            <IssueAssignmentDialog
               open={this.state.assignmentDialogOpen}
               issue={this._fakeIssue()}
               staff={this.state.staff || []}
               teams={this.state.teams || []}
               currentCaption={currentAssignmentCaption}
               saving={false}
               onClose={() => this.setState({ assignmentDialogOpen: false })}
               onSave={(assignment) => this.setState({ assignment, assignmentDialogOpen: false })}
               onUnassign={() => this.setState({ assignment: null, assignmentDialogOpen: false })}
            />
            <IssueTagsDialog
               open={this.state.tagsDialogOpen}
               issue={this._fakeIssue()}
               tags={tags}
               saving={false}
               creating={false}
               allowCreate
               onClose={() => this.setState({ tagsDialogOpen: false })}
               onSave={(selectedTags) => this.setState({ selectedTags, tagsDialogOpen: false })}
               onCreate={this._createLocalTag}
            />
         </Dialog>
      );
   }
}

export default IssueNew;
